import React, { FC } from "react";
import { Field, FormSection, formValueSelector } from "@mds/common/components/forms/form";
import { Row, Col } from "antd";
import {
  getNoticeOfWorkApplicationProgressStatusCodeOptions,
  getMineRegionDropdownOptions,
  getDropdownNoticeOfWorkApplicationTypeOptions,
  getDropdownNoticeOfWorkApplicationPermitTypeOptions,
  getMineRegionHash,
  getNoticeOfWorkApplicationPermitTypeOptionsHash,
  getNoticeOfWorkApplicationTypeOptionsHash,
} from "@mds/common/redux/selectors/staticContentSelectors";
import { getUserAccessData } from "@mds/common/redux/selectors/authenticationSelectors";
import {
  required,
  lat,
  lon,
  maxLength,
  requiredRadioButton,
  number,
  min,
  max,
  wholeNumber,
} from "@mds/common/redux/utils/Validate";
import * as Strings from "@mds/common/constants/strings";
import { USER_ROLES } from "@mds/common/constants/environment";
import { getNoticeOfWorkEditableTypes } from "@mds/common/redux/selectors/noticeOfWorkSelectors";
import RenderField from "@mds/common/components/forms/RenderField";
import RenderRadioButtons from "@mds/common/components/forms/RenderRadioButtons";
import RenderAutoSizeField from "@mds/common/components/forms/RenderAutoSizeField";
import RenderSelect from "@mds/common/components/forms/RenderSelect";
import RenderDate from "@mds/common/components/forms/RenderDate";
import * as FORM from "@/constants/forms";
import { OPEN_NEW_TAB } from "@/constants/assets";
import ScrollContentWrapper from "@/components/noticeOfWork/applications/ScrollContentWrapper";
import ReviewActivities from "@/components/noticeOfWork/applications/review/ReviewActivities";
import NOWSubmissionDocuments from "@/components/noticeOfWork/applications//NOWSubmissionDocuments";
import { NOWOriginalValueTooltip, NOWFieldOriginTooltip } from "@/components/common/CoreTooltip";
import ReviewApplicationFeeContent from "@/components/noticeOfWork/applications/review/ReviewApplicationFeeContent";
import * as Permission from "@/constants/permissions";
import ReviewNOWContacts from "./ReviewNOWContacts";
import ReclamationSummary from "./activities/ReclamationSummary";
import FormWrapper from "@mds/common/components/forms/FormWrapper";
import { useAppSelector } from "@mds/common/redux/rootState";

/**
 * @constant ReviewNOWApplication renders edit/view for the NoW Application review step
 */

interface ReviewNOWApplicationProps {
  isViewMode: boolean;
  reclamationSummary: any[];
  importNowSubmissionDocumentsJob: any;
  noticeOfWorkType: string;
  renderOriginalValues: any;
  noticeOfWork: any;
  initialValues: any;
  isPreLaunch: boolean;
  isNoticeOfWorkTypeDisabled: boolean;
}

const selector = formValueSelector(FORM.EDIT_NOTICE_OF_WORK);

export const ReviewNOWApplication: FC<ReviewNOWApplicationProps> = (
  props
) => {
  const contacts = useAppSelector((state) => selector(state, "contacts"));
  const now_application_guid = useAppSelector((state) => selector(state, "now_application_guid"));
  const documents = useAppSelector((state) => selector(state, "documents"));
  const submission_documents = useAppSelector((state) => selector(state, "submission_documents"));
  const imported_submission_documents = useAppSelector((state) => selector(state, "imported_submission_documents"));
  const filtered_submission_documents = useAppSelector((state) => selector(state, "filtered_submission_documents"));
  const proposedTonnage = useAppSelector((state) => selector(state, "proposed_annual_maximum_tonnage"));
  const adjustedTonnage = useAppSelector((state) => selector(state, "adjusted_annual_maximum_tonnage"));
  const proposedStartDate = useAppSelector((state) => selector(state, "proposed_start_date"));
  const proposedAuthorizationEndDate = useAppSelector((state) => selector(state, "proposed_end_date"));
  const typeOfApplication = useAppSelector((state) => selector(state, "type_of_application"));
  const applicationPermitType = useAppSelector((state) => selector(state, "application_permit_type_code"));
  const surfaceDisturbance = useAppSelector((state) => selector(state, "has_surface_disturbance_outside_tenure"));
  const isOnPrivateLand = useAppSelector((state) => selector(state, "state_of_land.is_on_private_land"));
  const activitiesInPark = useAppSelector((state) => selector(state, "state_of_land.has_activity_in_park"));
  const lieutenantGovernorAuthorization = useAppSelector(
    (state) => selector(state, "state_of_land.has_auth_lieutenant_gov_council")
  );
  const archaeologySitesAffected = useAppSelector((state) => selector(state, "state_of_land.has_archaeology_sites_affected"));
  const acknowledgedUNDRIP = useAppSelector((state) => selector(state, "state_of_land.has_acknowledged_undrip"));
  const isOnCrownLand = useAppSelector((state) => selector(state, "state_of_land.is_on_crown_land"));
  const hasLicenceOfOccupation = useAppSelector((state) => selector(state, "state_of_land.has_licence_of_occupation"));
  const appliedLicenceOccupation = useAppSelector(
    (state) => selector(state, "state_of_land.applied_for_licence_of_occupation")
  );
  const isAccessGated = useAppSelector((state) => selector(state, "is_access_gated"));
  const regionDropdownOptions = useAppSelector(getMineRegionDropdownOptions);
  const applicationTypeOptions = useAppSelector(getDropdownNoticeOfWorkApplicationTypeOptions);
  const applicationProgressStatusCodes = useAppSelector(getNoticeOfWorkApplicationProgressStatusCodeOptions);
  const permitTypeOptions = useAppSelector(getDropdownNoticeOfWorkApplicationPermitTypeOptions);
  const regionHash = useAppSelector(getMineRegionHash);
  const permitTypeHash = useAppSelector(getNoticeOfWorkApplicationPermitTypeOptionsHash);
  const applicationTypeOptionsHash = useAppSelector(getNoticeOfWorkApplicationTypeOptionsHash);
  const userRoles = useAppSelector(getUserAccessData);
  const editableApplicationTypeOptions = useAppSelector(getNoticeOfWorkEditableTypes);

  const isAdmin = userRoles.includes(USER_ROLES[Permission.ADMIN]);

  const renderCodeValues = (codeHash, value) => {
    if (value === Strings.EMPTY_FIELD) {
      return value;
    }
    return codeHash[value];
  };

  const applicationFileTableDescription =
    "In this table, you can see all documents submitted during initial application, revision and new files requested from the proponent. Documents added in this section will not show up in the permit package unless otherwise specified.";

  const applicationFilesTypes = ["AAF", "AEF", "MDO", "SDO"];

  const generateMinistryInspectionMapperUrl = (nowApplicationGuid) => {
    const queryString = encodeURIComponent(
      `bcgw_pub_whse_mineral_tenure_8797,NOW_APPLICATION_GUID,${nowApplicationGuid}`
    );
    return `${Strings.MINISTRY_INSPECTION_MAPPER_BASE_URL}&query=${queryString}`;
  };

  const renderMineInfo = () => (
    <div>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <div className="field-title">
            Name of Property
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("property_name").value}
              isVisible={props.renderOriginalValues("property_name").edited}
            />
          </div>
          <Field
            id="property_name"
            name="property_name"
            component={RenderField}
            required
            disabled={props.isViewMode}
            validate={[required, maxLength(4000)]}
          />
          <div className="field-title">
            Tenure Number(s)
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("tenure_number").value}
              isVisible={props.renderOriginalValues("tenure_number").edited}
            />
          </div>
          <Field
            id="tenure_number"
            name="tenure_number"
            component={RenderAutoSizeField}
            disabled={props.isViewMode}
            validate={[maxLength(4000)]}
          />
          <div className="field-title">
            Crown Grant / District Lot Number
            <NOWOriginalValueTooltip
              originalValue={
                props.renderOriginalValues("crown_grant_or_district_lot_numbers").value
              }
              isVisible={props.renderOriginalValues("crown_grant_or_district_lot_numbers").edited}
            />
          </div>
          <Field
            id="crown_grant_or_district_lot_numbers"
            name="crown_grant_or_district_lot_numbers"
            component={RenderAutoSizeField}
            disabled={props.isViewMode}
          />
          <div className="field-title">
            Directions to the site from the nearest municipality
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("directions_to_site").value}
              isVisible={props.renderOriginalValues("directions_to_site").edited}
            />
          </div>
          <Field
            id="directions_to_site"
            name="directions_to_site"
            component={RenderAutoSizeField}
            disabled={props.isViewMode}
            validate={[maxLength(4000)]}
          />
        </Col>
        <Col md={12} sm={24}>
          <div className="field-title">
            Lat
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("latitude").value}
              isVisible={props.renderOriginalValues("latitude").edited}
            />
          </div>
          <Field id="latitude" name="latitude" component={RenderField} disabled validate={[lat]} />
          <div className="field-title">
            Long
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("longitude").value}
              isVisible={props.renderOriginalValues("longitude").edited}
            />
          </div>
          <Field
            id="longitude"
            name="longitude"
            component={RenderField}
            disabled
            validate={[lon]}
          />
          <div className="field-title">
            Region
            <NOWOriginalValueTooltip
              originalValue={renderCodeValues(
                regionHash,
                props.renderOriginalValues("mine_region").value
              )}
              isVisible={props.renderOriginalValues("mine_region").edited}
            />
          </div>
          <Field
            id="mine_region"
            name="mine_region"
            component={RenderSelect}
            data={regionDropdownOptions}
            disabled
          />
          <div className="field-title">
            Proposed Annual Maximum Tonnage
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
          </div>
          <Field
            id="proposed_annual_maximum_tonnage"
            name="proposed_annual_maximum_tonnage"
            component={RenderField}
            validate={[number]}
            disabled
          />
          <div className="field-title">
            Open and view NoW on the Inspection Mapper (new window)&nbsp;&nbsp;
            <a
              href={generateMinistryInspectionMapperUrl(now_application_guid)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img alt="Open link in new window" src={OPEN_NEW_TAB} style={{ width: "1.25em" }} />
            </a>
          </div>
        </Col>
      </Row>
    </div>
  );

  const renderPermitType = () => (
    <Row gutter={16}>
      <Col md={12} sm={24}>
        <Field
          label="Years sought for authorization to complete this work"
          id="term_of_application"
          name="term_of_application"
          component={RenderField}
          required
          disabled={props.isViewMode}
          validate={[required, number, min(1), max(99), wholeNumber]}
        />
      </Col>
      <Col md={12} sm={24}>
        <div className="field-title">
          Permit Type
          <NOWOriginalValueTooltip
            originalValue={renderCodeValues(
              permitTypeHash,
              props.renderOriginalValues("application_permit_type_code").value
            )}
            isVisible={props.renderOriginalValues("application_permit_type_code").edited}
          />
        </div>
        <Field
          id="application_permit_type_code"
          name="application_permit_type_code"
          component={RenderSelect}
          data={permitTypeOptions}
          disabled={props.isViewMode}
        />
      </Col>
      <Col md={12} sm={24}>
        <Field
          label="First year of multi-year, area based application?"
          id="is_first_year_of_multi"
          name="is_first_year_of_multi"
          component={RenderRadioButtons}
          disabled={props.isViewMode}
          required
          validate={[requiredRadioButton]}
          data={[
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ]}
        />
      </Col>
      <Col md={12} sm={24}>
        {applicationPermitType === "MY-ABP" && (
          <>
            <div className="field-title">
              Is this the first year of a multi-year, area based application?
              <NOWOriginalValueTooltip
                originalValue={renderCodeValues(
                  permitTypeHash,
                  props.renderOriginalValues("is_first_year_of_multi").value
                )}
                isVisible={props.renderOriginalValues("is_first_year_of_multi").edited}
              />
            </div>
            <Field
              id="is_first_year_of_multi"
              name="is_first_year_of_multi"
              component={RenderRadioButtons}
              disabled={props.isViewMode}
            />
          </>
        )}
      </Col>
    </Row>
  );

  const renderAppInfo = () => {
    const noticeOfWorkTypeDropDownDisabled = props.isViewMode || props.isNoticeOfWorkTypeDisabled;

    const filteredApplicationTypeOptions = noticeOfWorkTypeDropDownDisabled
      ? applicationTypeOptions
      : editableApplicationTypeOptions;
    return (
      <div>
        <Row gutter={16}>
          <Col md={12} sm={24}>
            <div className="field-title">
              Type of Notice of Work
              <NOWOriginalValueTooltip
                originalValue={renderCodeValues(
                  applicationTypeOptionsHash,
                  props.renderOriginalValues("notice_of_work_type_code").value
                )}
                isVisible={props.renderOriginalValues("notice_of_work_type_code").edited}
              />
            </div>
            <Field
              id="notice_of_work_type_code"
              name="notice_of_work_type_code"
              component={RenderSelect}
              data={filteredApplicationTypeOptions}
              disabled={noticeOfWorkTypeDropDownDisabled}
              required
              validate={[required]}
            />
          </Col>
          <Col md={12} sm={24}>
            <div className="field-title">
              Mine Number
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("mine_no").value}
                isVisible={props.renderOriginalValues("mine_no").edited}
              />
            </div>
            <Field id="mine_no" name="mine_no" component={RenderField} disabled />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} sm={24}>
            <div className="field-title">
              Type of Application
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("type_of_application").value}
                isVisible={props.renderOriginalValues("type_of_application").edited}
              />
            </div>
            <Field
              id="type_of_application"
              name="type_of_application"
              component={RenderField}
              disabled
            />
          </Col>
          <Col md={12} sm={24}>
            <Field
              label="This mine is proposed for:"
              id="mine_purpose"
              name="mine_purpose"
              component={RenderRadioButtons}
              disabled={props.isViewMode}
              customOptions={[
                { label: "Exploration Activities", value: "Exploration Activities" },
                { label: "Production Mining", value: "Production Mining" },
                { label: "Both", value: "Both" },
              ]}
            />
          </Col>
          <Col md={12} sm={24}>
            {typeOfApplication !== "New Permit" && (
              <>
                <div className="field-title">
                  Permit Number
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("proponent_submitted_permit_number").value
                    }
                    isVisible={
                      props.renderOriginalValues("proponent_submitted_permit_number").edited
                    }
                  />
                </div>

                <Field
                  id="proponent_submitted_permit_number"
                  name="proponent_submitted_permit_number"
                  component={RenderField}
                  disabled
                />
              </>
            )}
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} sm={24}>
            {typeOfApplication !== "New Permit" && (
              <>
                <div className="field-title">
                  Annual Summary submitted for this site?
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={props.renderOriginalValues("annual_summary_submitted").value}
                    isVisible={props.renderOriginalValues("annual_summary_submitted").edited}
                  />
                </div>
                <Field
                  id="annual_summary_submitted"
                  name="annual_summary_submitted"
                  component={RenderRadioButtons}
                  disabled={props.isViewMode}
                />
              </>
            )}
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} sm={24}>
            <div className="field-title">
              ATS Authorization Number
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("ats_authorization_number").value}
                isVisible={props.renderOriginalValues("ats_authorization_number").edited}
              />
            </div>
            <Field
              id="ats_authorization_number"
              name="ats_authorization_number"
              component={RenderField}
              disabled={props.isViewMode || !isAdmin}
            />
          </Col>
          <Col md={12} sm={24}>
            <div className="field-title">
              Tracking Number
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("now_tracking_number").value}
                isVisible={props.renderOriginalValues("now_tracking_number").edited}
              />
            </div>
            <Field
              id="now_tracking_number"
              name="now_tracking_number"
              component={RenderField}
              disabled
            />
          </Col>
          <Col md={12} sm={24}>
            <Field
              label="Life of the Mine"
              id="term_of_application"
              name="term_of_application"
              component={RenderField}
              disabled
              validate={[min(1), max(99), wholeNumber]}
            />
          </Col>
          <Col md={12} sm={24}>
            <div className="field-title">
              ATS Project Number
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("ats_project_number").value}
                isVisible={props.renderOriginalValues("ats_project_number").edited}
              />
            </div>
            <Field
              id="ats_project_number"
              name="ats_project_number"
              component={RenderField}
              disabled={props.isViewMode || !isAdmin}
            />
          </Col>
        </Row>
      </div>
    );
  };

  const renderApplicantInfo = () => (
    <Row gutter={16}>
      <Col md={12} sm={24}>
        <div className="field-title">
          Individual or Company/Organization?
          {props.isPreLaunch && <NOWFieldOriginTooltip />}
          <NOWOriginalValueTooltip
            originalValue={props.renderOriginalValues("is_applicant_individual_or_company").value}
            isVisible={props.renderOriginalValues("is_applicant_individual_or_company").edited}
          />
        </div>
        <Field
          id="is_applicant_individual_or_company"
          name="is_applicant_individual_or_company"
          component={RenderField}
          disabled={props.isViewMode}
        />
        <div className="field-title">
          Relationship to Individual or Company/Organization?
          {props.isPreLaunch && <NOWFieldOriginTooltip />}
          <NOWOriginalValueTooltip
            originalValue={props.renderOriginalValues("relationship_to_applicant").value}
            isVisible={props.renderOriginalValues("relationship_to_applicant").edited}
          />
        </div>
        <Field
          id="relationship_to_applicant"
          name="relationship_to_applicant"
          component={RenderField}
          disabled={props.isViewMode}
        />
      </Col>
      <Col md={12} sm={24}>
        <ReviewApplicationFeeContent
          initialValues={props.noticeOfWork}
          isViewMode={props.isViewMode}
          isAdmin={isAdmin}
          proposedTonnage={proposedTonnage}
          adjustedTonnage={adjustedTonnage}
          proposedStartDate={proposedStartDate}
          proposedAuthorizationEndDate={proposedAuthorizationEndDate}
          isPreLaunch={props.isPreLaunch}
        />
      </Col>
    </Row>
  );

  const renderAccess = () => (
    <div>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <div className="field-title">
            Access presently gated
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
            <NOWOriginalValueTooltip
              style={{ marginLeft: "5%" }}
              originalValue={props.renderOriginalValues("is_access_gated").value}
              isVisible={props.renderOriginalValues("is_access_gated").edited}
            />
          </div>
          <Field
            id="is_access_gated"
            name="is_access_gated"
            component={RenderRadioButtons}
            disabled={props.isViewMode}
          />
        </Col>
        {isAccessGated && (
          <Col md={12} sm={24}>
            <div className="field-title">
              Key provided to the inspector
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
              <NOWOriginalValueTooltip
                style={{ marginLeft: "5%" }}
                originalValue={props.renderOriginalValues("has_key_for_inspector").value}
                isVisible={props.renderOriginalValues("has_key_for_inspector").edited}
              />
            </div>
            <Field
              id="has_key_for_inspector"
              name="has_key_for_inspector"
              component={RenderRadioButtons}
              disabled={props.isViewMode}
            />
          </Col>
        )}
      </Row>
    </div>
  );

  const renderAccessToTenure = () => (
    <Row gutter={16}>
      <Col md={12} sm={24}>
        <div className="field-title">
          Do you need to build a road, create stream crossings or other surface disturbance that
          will not be on your tenure?
          {props.isPreLaunch && <NOWFieldOriginTooltip />}
          <NOWOriginalValueTooltip
            originalValue={
              props.renderOriginalValues("has_surface_disturbance_outside_tenure").value
            }
            isVisible={props.renderOriginalValues("has_surface_disturbance_outside_tenure").edited}
          />
        </div>
        <Field
          id="has_surface_disturbance_outside_tenure"
          name="has_surface_disturbance_outside_tenure"
          component={RenderRadioButtons}
          disabled={props.isViewMode}
        />
      </Col>
      {surfaceDisturbance && (
        <>
          <Col md={12} sm={24}>
            <div className="field-title">
              Do you have the required access authorizations in place?
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
              <NOWOriginalValueTooltip
                style={{ marginLeft: "5%" }}
                originalValue={props.renderOriginalValues("has_req_access_authorizations").value}
                isVisible={props.renderOriginalValues("has_req_access_authorizations").edited}
              />
            </div>
            <Field
              id="has_req_access_authorizations"
              name="has_req_access_authorizations"
              component={RenderRadioButtons}
              disabled={props.isViewMode}
            />
          </Col>
          <Col md={12} sm={24}>
            <div className="field-title">
              Provide the type and authorization numbers for each access authorization application
              or exemption to use the road(s).
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
              <NOWOriginalValueTooltip
                originalValue={props.renderOriginalValues("req_access_authorization_numbers").value}
                isVisible={props.renderOriginalValues("req_access_authorization_numbers").edited}
              />
            </div>
            <Field
              id="req_access_authorization_numbers"
              name="req_access_authorization_numbers"
              component={RenderAutoSizeField}
              disabled={props.isViewMode}
            />
          </Col>
        </>
      )}
    </Row>
  );

  const renderStateOfLand = () => (
    <div>
      <FormSection name="state_of_land">
        <ScrollContentWrapper id="state-of-land" title="Present State of Land">
          <>
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Present condition of the land
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.present_land_condition_description")
                        .value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.present_land_condition_description")
                        .edited
                    }
                  />
                </div>
                <Field
                  id="present_land_condition_description"
                  name="present_land_condition_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Current means of access
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.means_of_access_description").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.means_of_access_description").edited
                    }
                  />
                </div>
                <Field
                  id="means_of_access_description"
                  name="means_of_access_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Physiography
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.physiography_description").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.physiography_description").edited
                    }
                  />
                </div>
                <Field
                  id="physiography_description"
                  name="physiography_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Old Equipment
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.old_equipment_description").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.old_equipment_description").edited
                    }
                  />
                </div>
                <Field
                  id="old_equipment_description"
                  name="old_equipment_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Type of vegetation
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.type_of_vegetation_description")
                        .value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.type_of_vegetation_description")
                        .edited
                    }
                  />
                </div>
                <Field
                  id="type_of_vegetation_description"
                  name="type_of_vegetation_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Recreational trail/use
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.recreational_trail_use_description")
                        .value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.recreational_trail_use_description")
                        .edited
                    }
                  />
                </div>
                <Field
                  id="recreational_trail_use_description"
                  name="recreational_trail_use_description"
                  component={RenderAutoSizeField}
                  disabled={props.isViewMode}
                />
              </Col>
            </Row>
          </>
        </ScrollContentWrapper>
        <ScrollContentWrapper id="land-ownership" title="Land Ownership">
          <>
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Application in a community watershed
                  <NOWOriginalValueTooltip
                    style={{ marginLeft: "5%" }}
                    originalValue={
                      props.renderOriginalValues("state_of_land.has_community_water_shed").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.has_community_water_shed").edited
                    }
                  />
                </div>
                <Field
                  id="has_community_water_shed"
                  name="has_community_water_shed"
                  component={RenderRadioButtons}
                  disabled={props.isViewMode}
                  required
                  validate={[requiredRadioButton]}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Proposed activities on private land
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    style={{ marginLeft: "5%" }}
                    originalValue={
                      props.renderOriginalValues("state_of_land.is_on_private_land").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.is_on_private_land").edited
                    }
                  />
                </div>
                <Field
                  id="is_on_private_land"
                  name="is_on_private_land"
                  component={RenderRadioButtons}
                  disabled={props.isViewMode}
                />
              </Col>
            </Row>
            {isOnPrivateLand && (
              <Row gutter={16}>
                <Col md={12} sm={24}>
                  <div className="field-title">
                    Has notice been served to all parties on private lands?
                    {props.isPreLaunch && <NOWFieldOriginTooltip />}
                    <NOWOriginalValueTooltip
                      style={{ marginLeft: "5%" }}
                      originalValue={
                        props.renderOriginalValues("state_of_land.notice_served_to_private").value
                      }
                      isVisible={
                        props.renderOriginalValues("state_of_land.notice_served_to_private").edited
                      }
                    />
                  </div>
                  <Field
                    id="notice_served_to_private"
                    name="notice_served_to_private"
                    component={RenderRadioButtons}
                    disabled={props.isViewMode}
                  />
                </Col>
                <Col md={12} sm={24}>
                  <div className="field-title">
                    Legal Description of the land
                    {props.isPreLaunch && <NOWFieldOriginTooltip />}
                    <NOWOriginalValueTooltip
                      originalValue={
                        props.renderOriginalValues("state_of_land.legal_description_land").value
                      }
                      isVisible={
                        props.renderOriginalValues("state_of_land.legal_description_land").edited
                      }
                    />
                  </div>
                  <Field
                    id="legal_description_land"
                    name="legal_description_land"
                    component={RenderAutoSizeField}
                    disabled={props.isViewMode}
                    validate={[maxLength(4000)]}
                  />
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Proposed activities on crown land
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    style={{ marginLeft: "5%" }}
                    originalValue={
                      props.renderOriginalValues("state_of_land.is_on_crown_land").value
                    }
                    isVisible={props.renderOriginalValues("state_of_land.is_on_crown_land").edited}
                  />
                </div>
                <Field
                  id="is_on_crown_land"
                  name="is_on_crown_land"
                  component={RenderRadioButtons}
                  disabled={props.isViewMode}
                />
              </Col>
            </Row>
            {isOnCrownLand && (
              <>
                <Row gutter={16}>
                  <Col md={12} sm={24}>
                    <div className="field-title">
                      Do you have licence of Occupation?
                      {props.isPreLaunch && <NOWFieldOriginTooltip />}
                      <NOWOriginalValueTooltip
                        style={{ marginLeft: "5%" }}
                        originalValue={
                          props.renderOriginalValues("state_of_land.has_licence_of_occupation")
                            .value
                        }
                        isVisible={
                          props.renderOriginalValues("state_of_land.has_licence_of_occupation")
                            .edited
                        }
                      />
                    </div>
                    <Field
                      id="has_licence_of_occupation"
                      name="has_licence_of_occupation"
                      component={RenderRadioButtons}
                      disabled={props.isViewMode}
                    />
                  </Col>
                </Row>
                {hasLicenceOfOccupation && (
                  <Row gutter={16}>
                    <Col md={12} sm={24}>
                      <div className="field-title">
                        Licence of Occupation number(s)
                        {props.isPreLaunch && <NOWFieldOriginTooltip />}
                        <NOWOriginalValueTooltip
                          originalValue={
                            props.renderOriginalValues("state_of_land.licence_of_occupation").value
                          }
                          isVisible={
                            props.renderOriginalValues("state_of_land.licence_of_occupation").edited
                          }
                        />
                      </div>
                      <Field
                        id="licence_of_occupation"
                        name="licence_of_occupation"
                        component={RenderField}
                        disabled={props.isViewMode}
                      />
                    </Col>
                  </Row>
                )}
                {!hasLicenceOfOccupation && hasLicenceOfOccupation !== undefined && (
                  <>
                    <Row gutter={16}>
                      <Col md={12} sm={24}>
                        <div className="field-title">
                          Have you applied for a Licence of Occupation?
                          {props.isPreLaunch && <NOWFieldOriginTooltip />}
                          <NOWOriginalValueTooltip
                            originalValue={
                              props.renderOriginalValues(
                                "state_of_land.applied_for_licence_of_occupation"
                              ).value
                            }
                            isVisible={
                              props.renderOriginalValues(
                                "state_of_land.applied_for_licence_of_occupation"
                              ).edited
                            }
                          />
                        </div>
                        <Field
                          id="applied_for_licence_of_occupation"
                          name="applied_for_licence_of_occupation"
                          component={RenderRadioButtons}
                          disabled={props.isViewMode}
                        />
                      </Col>
                    </Row>
                    {appliedLicenceOccupation && (
                      <Row gutter={16}>
                        <Col md={12} sm={24}>
                          <div className="field-title">
                            File number of Application
                            {props.isPreLaunch && <NOWFieldOriginTooltip />}
                            <NOWOriginalValueTooltip
                              originalValue={
                                props.renderOriginalValues("state_of_land.file_number_of_app").value
                              }
                              isVisible={
                                props.renderOriginalValues("state_of_land.file_number_of_app")
                                  .edited
                              }
                            />
                          </div>
                          <Field
                            id="file_number_of_app"
                            name="file_number_of_app"
                            component={RenderField}
                            disabled={props.isViewMode}
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </>
            )}
            <br />
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <div className="field-title">
                  Are any of the proposed activities in a park under an Act of British Columbia or
                  of Canada or in an area of land established as a Provincial Heritage property
                  under Section 23 of the <i>Heritage Conservation Act</i>?
                  {props.isPreLaunch && <NOWFieldOriginTooltip />}
                  <NOWOriginalValueTooltip
                    originalValue={
                      props.renderOriginalValues("state_of_land.has_activity_in_park").value
                    }
                    isVisible={
                      props.renderOriginalValues("state_of_land.has_activity_in_park").edited
                    }
                  />
                </div>
                <Field
                  id="has_activity_in_park"
                  name="has_activity_in_park"
                  component={RenderRadioButtons}
                  disabled={props.isViewMode}
                />
              </Col>
              {activitiesInPark && (
                <Col md={12} sm={24}>
                  <div className="field-title">
                    Do you have authorization by the Lieutenant Governor in Council?
                    {props.isPreLaunch && <NOWFieldOriginTooltip />}
                    <NOWOriginalValueTooltip
                      style={{ marginLeft: "5%" }}
                      originalValue={
                        props.renderOriginalValues("state_of_land.has_auth_lieutenant_gov_council")
                          .value
                      }
                      isVisible={
                        props.renderOriginalValues("state_of_land.has_auth_lieutenant_gov_council")
                          .edited
                      }
                    />
                  </div>
                  <Field
                    id="has_auth_lieutenant_gov_council"
                    name="has_auth_lieutenant_gov_council"
                    component={RenderRadioButtons}
                    disabled={props.isViewMode}
                  />
                </Col>
              )}
            </Row>
            {lieutenantGovernorAuthorization && activitiesInPark && (
              <Row gutter={16}>
                <Col md={12} sm={24}>
                  <div className="field-title">
                    Details of the Authorization
                    {props.isPreLaunch && <NOWFieldOriginTooltip />}
                    <NOWOriginalValueTooltip
                      originalValue={
                        props.renderOriginalValues("state_of_land.authorization_details").value
                      }
                      isVisible={
                        props.renderOriginalValues("state_of_land.authorization_details").edited
                      }
                    />
                  </div>
                  <Field
                    id="authorization_details"
                    name="authorization_details"
                    component={RenderField}
                    disabled={props.isViewMode}
                  />
                </Col>
              </Row>
            )}
          </>
        </ScrollContentWrapper>
        <ScrollContentWrapper id="cultural-heritage-resources" title="Cultural Heritage Resources">
          <Row gutter={16}>
            <Col md={12} sm={24}>
              <div className="field-title">
                Protection of Cultural Heritage Resources
                {props.isPreLaunch && <NOWFieldOriginTooltip />}
                <NOWOriginalValueTooltip
                  originalValue={
                    props.renderOriginalValues(
                      "state_of_land.protection_of_cultural_heritage_resources"
                    ).value
                  }
                  isVisible={
                    props.renderOriginalValues(
                      "state_of_land.protection_of_cultural_heritage_resources"
                    ).edited
                  }
                />
              </div>
              <Field
                id="protection_of_cultural_heritage_resources"
                name="protection_of_cultural_heritage_resources"
                component={RenderAutoSizeField}
                disabled={props.isViewMode}
                validate={[maxLength(4000)]}
              />
            </Col>
          </Row>
        </ScrollContentWrapper>
      </FormSection>
    </div>
  );

  const renderFirstAid = () => (
    <div>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <div className="field-title">
            Proposed First Aid equipment on-site
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("first_aid_equipment_on_site").value}
              isVisible={props.renderOriginalValues("first_aid_equipment_on_site").edited}
            />
          </div>
          <Field
            id="first_aid_equipment_on_site"
            name="first_aid_equipment_on_site"
            component={RenderAutoSizeField}
            disabled={props.isViewMode}
          />
        </Col>
        <Col md={12} sm={24}>
          <div className="field-title">
            Level of First Aid Certificate held by attendant
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("first_aid_cert_level").value}
              isVisible={props.renderOriginalValues("first_aid_cert_level").edited}
            />
          </div>
          <Field
            id="first_aid_cert_level"
            name="first_aid_cert_level"
            component={RenderField}
            disabled={props.isViewMode}
          />
        </Col>
      </Row>
    </div>
  );

  const renderWorkPlan = () => (
    <Row gutter={16}>
      <Col md={24} sm={24}>
        <div className="field-title">
          Sufficient details of your work program to enable a good understanding of the types and
          scope of the activities that will be conducted:
          {props.isPreLaunch && <NOWFieldOriginTooltip />}
          <NOWOriginalValueTooltip
            originalValue={props.renderOriginalValues("work_plan").value}
            isVisible={props.renderOriginalValues("work_plan").edited}
          />
        </div>
        <Field
          id="work_plan"
          name="work_plan"
          component={RenderAutoSizeField}
          disabled={props.isViewMode}
        />
      </Col>
    </Row>
  );

  const renderReclamation = () => (
    <div>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <div className="field-title">
            Total merchantable timber volume
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
            <NOWOriginalValueTooltip
              originalValue={
                props.renderOriginalValues(
                  "merchantable_timber_volume",
                  "total_merchantable_timber_volume"
                ).value
              }
              isVisible={
                props.renderOriginalValues(
                  "merchantable_timber_volume",
                  "total_merchantable_timber_volume"
                ).edited
              }
            />
          </div>
          <Field
            id="total_merchantable_timber_volume"
            name="total_merchantable_timber_volume"
            component={RenderField}
            disabled
          />
        </Col>
      </Row>
      <br />
      <ReclamationSummary summary={props.reclamationSummary} />
      <br />
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <div className="field-title">
            Unreclaimed disturbance from previous year (ha)
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
            <NOWOriginalValueTooltip
              originalValue={
                props.renderOriginalValues("unreclaimed_disturbance_previous_year").value
              }
              isVisible={props.renderOriginalValues("unreclaimed_disturbance_previous_year").edited}
            />
          </div>
          <Field
            id="unreclaimed_disturbance_previous_year"
            name="unreclaimed_disturbance_previous_year"
            component={RenderField}
            disabled={props.isViewMode}
            validate={[number]}
          />
        </Col>
        <Col md={12} sm={24}>
          <div className="field-title">
            Disturbance planned for reclamation this year (ha)
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("disturbance_planned_reclamation").value}
              isVisible={props.renderOriginalValues("disturbance_planned_reclamation").edited}
            />
          </div>
          <Field
            id="disturbance_planned_reclamation"
            name="disturbance_planned_reclamation"
            component={RenderField}
            disabled={props.isViewMode}
            validate={[number]}
          />
        </Col>
      </Row>
    </div>
  );

  const renderProposedDates = () => (
    <Row gutter={16}>
      <Col md={12} sm={24}>
        {typeOfApplication !== "New Permit" && (
          <>
            <div className="field-title">
              Original Start Date
              {props.isPreLaunch && <NOWFieldOriginTooltip />}
            </div>
            <Field
              id="original_start_date"
              name="original_start_date"
              component={RenderDate}
              disabled
            />
          </>
        )}
        <div className="field-title">Proposed Start Date</div>
        <Field
          id="proposed_start_date"
          name="proposed_start_date"
          component={RenderDate}
          disabled
        />
      </Col>
      <Col md={12} sm={24}>
        <div className="field-title">Proposed Authorization End Date</div>
        <Field id="proposed_end_date" name="proposed_end_date" component={RenderDate} disabled />
        <div className="field-title">Type of Activity</div>
        <Field id="work_year_info" name="work_year_info" component={RenderField} disabled />
      </Col>
    </Row>
  );

  const renderOtherInformation = () => (
    <div>
      <Row gutter={16}>
        <Col md={24}>
          <div className="field-title">
            Is there any other information you would like us to know (where possible, do not include
            personal information)
            {props.isPreLaunch && <NOWFieldOriginTooltip />}
            <NOWOriginalValueTooltip
              originalValue={props.renderOriginalValues("other_information").value}
              isVisible={props.renderOriginalValues("other_information").edited}
            />
          </div>
          <Field
            id="other_information"
            name="other_information"
            component={RenderAutoSizeField}
            disabled={props.isViewMode}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      <FormWrapper onSubmit={() => { }}
        initialValues={props.initialValues ?? {}}
        name={FORM.EDIT_NOTICE_OF_WORK}
        reduxFormConfig={{
          touchOnChange: false,
          touchOnBlur: true,
          enableReinitialize: true,
        }}
      >
        <ScrollContentWrapper id="applicant-info" title="Applicant Information">
          {renderApplicantInfo()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="application-info" title="Application Information">
          {renderAppInfo()}
        </ScrollContentWrapper>
        <ScrollContentWrapper
          id="permit-type"
          title="One Year, Multi-Year or Multi-Year Area Based Permit"
        >
          {renderPermitType()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="mine-info" title="Mine Information">
          {renderMineInfo()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="first-aid" title="First Aid">
          {renderFirstAid()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="work-plan" title="Description of Work Program">
          {renderWorkPlan()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="time-proposed-activities" title="Time of Proposed Activities">
          {renderProposedDates()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="access" title="Access">
          {renderAccess()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="access-to-tenure" title="Access to Tenure">
          {renderAccessToTenure()}
        </ScrollContentWrapper>
        {/* <ScrollContentWrapper id="state-of-land" title="State of Land"> */}
        {renderStateOfLand()}
        {/* </ScrollContentWrapper> */}
        <ReviewActivities
          isViewMode={props.isViewMode}
          noticeOfWorkType={props.noticeOfWorkType}
          noticeOfWork={props.initialValues}
          renderOriginalValues={props.renderOriginalValues}
          isPreLaunch={props.isPreLaunch}
        />
        <ScrollContentWrapper id="reclamation" title="Summary of Reclamation">
          {renderReclamation()}
        </ScrollContentWrapper>

        <ScrollContentWrapper id="contacts" title="Contacts">
          <ReviewNOWContacts
            contacts={props.noticeOfWork.contacts}
            isViewMode={props.isViewMode}
            contactFormValues={contacts}
            noticeOfWork={props.noticeOfWork}
          />
        </ScrollContentWrapper>
        <ScrollContentWrapper id="other-information" title="Other Information">
          {renderOtherInformation()}
        </ScrollContentWrapper>
        <ScrollContentWrapper id="application-files" title="Application Files">
          <NOWSubmissionDocuments
            now_application_guid={now_application_guid}
            documents={props.noticeOfWork.filtered_submission_documents.concat(
              props.noticeOfWork.documents
                ?.filter(
                  ({
                    now_application_document_sub_type_code,
                    now_application_document_type_code,
                    mine_document,
                  }) =>
                    applicationFilesTypes.includes(now_application_document_sub_type_code) &&
                    (now_application_document_type_code !== "PMT" ||
                      now_application_document_type_code !== "PMA" ||
                      mine_document.document_name.includes("DRAFT"))
                )
                .map((doc) => {
                  return {
                    preamble_author: doc.preamble_author,
                    preamble_date: doc.preamble_date,
                    preamble_title: doc.preamble_title,
                    now_application_document_xref_guid: doc.now_application_document_xref_guid,
                    is_referral_package: doc.is_referral_package,
                    is_final_package: doc.is_final_package,
                    is_consultation_package: doc.is_consultation_package,
                    description: doc.description,
                    mine_document_guid: doc.mine_document.mine_document_guid,
                    filename: doc.mine_document.document_name,
                    document_manager_guid: doc.mine_document.document_manager_guid,
                    notForImport: true,
                    ...doc,
                  };
                })
            )}
            importNowSubmissionDocumentsJob={props.importNowSubmissionDocumentsJob}
            disableCategoryFilter
            displayTableDescription
            tableDescription={applicationFileTableDescription}
            showDescription
            isViewMode={props.isViewMode}
          />
        </ScrollContentWrapper>
      </FormWrapper>
    </div>
  );
};

export default ReviewNOWApplication;